import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAlbums, deleteAlbum, getTracks, updateAlbum } from '../api';
import useAuthStore from '../store/authStore';
import usePlayerStore from '../store/playerStore';
import NavControls from '../components/NavControls';

const ALBUM_WIDTH = 180;
const GAP = 16;
const ROWS = 3;
const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

function getRandomLetter() {
  return String.fromCharCode(65 + Math.floor(Math.random() * 26));
}

function calcPageSize() {
  const isMobile = window.innerWidth <= 768;
  if (isMobile) return 24;
  const cols = Math.max(1, Math.floor((window.innerWidth - 220) / (ALBUM_WIDTH + GAP)));
  return cols * ROWS;
}

export default function Albums() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuthStore();
  const isAdmin = user?.is_staff === true;
  const queryClient = useQueryClient();
  const { playQueue, toggleShuffle, shuffle } = usePlayerStore();

  const [search, setSearch] = useState(() => searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [artistOnly, setArtistOnly] = useState(false);
  const [compilationsOnly, setCompilationsOnly] = useState(false);
  const [pageSize, setPageSize] = useState(calcPageSize);
  const [startLetter, setStartLetter] = useState(() => searchParams.get('letter') || getRandomLetter());
  const [randomSeed, setRandomSeed] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [isShuffling, setIsShuffling] = useState(false);
  const [shuffleCount, setShuffleCount] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const editInputRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setPageSize(calcPageSize());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Debounce search — fire query 300ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Keep URL in sync so Back to Albums restores the same view
  useEffect(() => {
    if (search) {
      setSearchParams({ search }, { replace: true });
    } else if (randomSeed !== null) {
      setSearchParams({}, { replace: true });
    } else {
      setSearchParams({ letter: startLetter }, { replace: true });
    }
  }, [search, startLetter, randomSeed]); // intentionally omitting setSearchParams from deps — stable

  const baseParams = compilationsOnly ? { is_compilation: true } : {};
  const queryParams = debouncedSearch
    ? artistOnly
      ? { ...baseParams, artist__name__icontains: debouncedSearch, ordering: 'artist__name,title', page_size: pageSize }
      : { ...baseParams, search: debouncedSearch, ordering: 'artist__name,title', page_size: pageSize }
    : randomSeed !== null
      ? { ...baseParams, random: true, page_size: pageSize }
      : { ...baseParams, ordering: 'title', title__istartswith: startLetter, page_size: pageSize };

  const { data, isLoading, isFetching } = useQuery(
    ['albums', debouncedSearch, artistOnly, compilationsOnly, startLetter, randomSeed, pageSize],
    () => getAlbums(queryParams).then(r => r.data),
    { keepPreviousData: true }
  );

  const albums = data?.results || [];

  const handleRandomize = () => {
    setRandomSeed(Date.now());
    setSearch('');
    setShuffleCount(null);
  };

  const handleLetterClick = (letter) => {
    setStartLetter(letter);
    setSearch('');
    setRandomSeed(null);
    setShuffleCount(null);
  };

  const handleShuffleAll = async () => {
    setIsShuffling(true);
    try {
      let params;
      if (search) {
        params = artistOnly
          ? { artist__name__icontains: search, page_size: 500 }
          : { search, page_size: 500 };
      } else if (randomSeed !== null) {
        params = { page_size: 500 };
      } else {
        params = { 'album__title__istartswith': startLetter, page_size: 500 };
      }
      if (compilationsOnly) params['album__is_compilation'] = true;
      const res = await getTracks(params);
      const tracks = res.data.results || [];
      if (!tracks.length) return;
      const shuffled = [...tracks].sort(() => Math.random() - 0.5);
      setShuffleCount(shuffled.length);
      if (!shuffle) toggleShuffle();
      playQueue(shuffled, 0);
    } finally {
      setIsShuffling(false);
    }
  };

  const handleDelete = async (e, albumId) => {
    e.stopPropagation();
    if (!window.confirm('Delete this album and all its tracks?')) return;
    setDeletingId(albumId);
    try {
      await deleteAlbum(albumId);
      queryClient.invalidateQueries('albums');
    } catch (err) {
      alert('Failed to delete album.');
    } finally {
      setDeletingId(null);
    }
  };

  const startEditTitle = (e, album) => {
    e.stopPropagation();
    setEditingId(album.id);
    setEditTitle(album.title);
    setTimeout(() => editInputRef.current?.focus(), 0);
  };

  const commitEditTitle = async (albumId) => {
    const trimmed = editTitle.trim();
    if (!trimmed) {
      setEditingId(null);
      return;
    }
    const album = albums.find(a => a.id === albumId);
    if (trimmed === album?.title) {
      setEditingId(null);
      return;
    }
    try {
      await updateAlbum(albumId, { title: trimmed });
      queryClient.invalidateQueries('albums');
    } catch (err) {
      alert('Failed to rename album. The title may already exist for this artist.');
    }
    setEditingId(null);
  };

  const handleTitleKeyDown = (e, albumId) => {
    if (e.key === 'Enter') commitEditTitle(albumId);
    else if (e.key === 'Escape') setEditingId(null);
  };

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h1>Albums & Artists</h1>
          <NavControls />
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <input
          className="search-input"
          placeholder="Search albums or artists…"
          value={search}
          onChange={e => { setSearch(e.target.value); setRandomSeed(null); setShuffleCount(null); }}
          style={{ maxWidth: 300 }}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-muted)', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={artistOnly}
            onChange={e => setArtistOnly(e.target.checked)}
          />
          Search artist only
        </label>
        <button
          className="btn btn-secondary"
          onClick={handleRandomize}
          disabled={isLoading || isFetching}
        >
          🎲 Randomize
        </button>
        <button
          className="btn btn-secondary"
          onClick={handleShuffleAll}
          disabled={isShuffling}
        >
          {isShuffling ? 'Loading…' : shuffleCount !== null ? `🔀 Shuffling ${shuffleCount} songs` : '🔀 Shuffle All'}
        </button>
        {!search && randomSeed === null && (
          <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>
            Showing albums starting with <strong style={{ color: 'var(--accent)' }}>{startLetter}</strong>
          </span>
        )}
        {randomSeed !== null && (
          <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>
            Showing random selection
          </span>
        )}
      </div>

      {/* A–Z letter bar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 20 }}>
        {LETTERS.map(letter => (
          <button
            key={letter}
            onClick={() => handleLetterClick(letter)}
            style={{
              padding: '3px 7px',
              fontSize: 13,
              fontWeight: 600,
              borderRadius: 5,
              border: '1px solid',
              cursor: 'pointer',
              borderColor: (!search && randomSeed === null && startLetter === letter)
                ? 'var(--accent)'
                : 'var(--border)',
              background: (!search && randomSeed === null && startLetter === letter)
                ? 'var(--accent)'
                : 'var(--bg2)',
              color: (!search && randomSeed === null && startLetter === letter)
                ? '#fff'
                : 'var(--text-muted)',
            }}
          >
            {letter}
          </button>
        ))}
      </div>

      {isLoading
        ? <p style={{ color: 'var(--text-muted)' }}>Loading…</p>
        : (
          <div className="cards-grid">
            {albums.map(album => (
              <div
                key={album.id}
                className="card"
                onClick={() => editingId !== album.id && navigate(`/albums/${album.id}`)}
                style={{ cursor: 'pointer', position: 'relative' }}
              >
                {album.cover_art_url
                  ? <img className="card-art" src={album.cover_art_url} alt={album.title} loading="lazy" />
                  : <div className="card-art" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 40 }}>💿</div>
                }
                <div className="card-info">
                  <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {editingId === album.id ? (
                      <input
                        ref={editInputRef}
                        value={editTitle}
                        onChange={e => setEditTitle(e.target.value)}
                        onBlur={() => commitEditTitle(album.id)}
                        onKeyDown={e => handleTitleKeyDown(e, album.id)}
                        onClick={e => e.stopPropagation()}
                        style={{
                          width: '100%',
                          fontSize: 'inherit',
                          fontWeight: 'inherit',
                          background: 'var(--bg)',
                          border: '1px solid var(--accent)',
                          borderRadius: 4,
                          color: 'var(--text)',
                          padding: '1px 4px',
                        }}
                      />
                    ) : (
                      <>
                        <span
                          title={isAdmin ? 'Click to rename' : undefined}
                          onClick={isAdmin ? (e) => startEditTitle(e, album) : undefined}
                          style={isAdmin ? { cursor: 'text', flex: 1 } : { flex: 1 }}
                        >
                          {album.title}
                        </span>
                        {album.is_compilation && (
                          <span style={{ fontSize: 10, background: 'var(--accent)', color: '#fff', borderRadius: 4, padding: '1px 5px', flexShrink: 0 }}>VA</span>
                        )}
                      </>
                    )}
                  </div>
                  <div className="card-sub">{album.is_compilation ? 'Various Artists' : album.artist_name}</div>
                  <div className="card-sub">{album.year} · {album.track_count} tracks</div>
                </div>
                {isAdmin && (
                  <button
                    onClick={(e) => handleDelete(e, album.id)}
                    disabled={deletingId === album.id}
                    title="Delete album"
                    style={{
                      position: 'absolute',
                      bottom: 8,
                      right: 8,
                      background: 'rgba(0,0,0,0.6)',
                      border: 'none',
                      borderRadius: 6,
                      color: deletingId === album.id ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.5)',
                      fontSize: 14,
                      cursor: deletingId === album.id ? 'not-allowed' : 'pointer',
                      padding: '4px 6px',
                      lineHeight: 1,
                    }}
                    onMouseEnter={e => { if (deletingId !== album.id) e.currentTarget.style.color = '#ff6b6b'; }}
                    onMouseLeave={e => { if (deletingId !== album.id) e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }}
                  >
                    🗑
                  </button>
                )}
              </div>
            ))}
          </div>
        )
      }
    </div>
  );
}
